import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Box,
  Paper,
  Typography,
  TextField
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ReplyIcon from '@mui/icons-material/Reply'
import ReplyAllIcon from '@mui/icons-material/ReplyAll'
import ForwardIcon from '@mui/icons-material/Forward'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import JoditEditor from 'jodit-react'
import DOMPurify from 'dompurify'
import { saveAs } from 'file-saver'
import { Buffer } from 'buffer'
import { toast } from 'react-toastify'
import { useAuth } from '../../context/AuthContext'
import ConfirmationButton from '../misc/ConfirmationButton'
import { addObject, deleteObject, getObject } from '../../api/apiClientCrud'
import { sendMail } from '../../utils/emailUtils'
import { fileToBase64 } from '../../utils/documentManager'

const isValidEmail = (email) => {
  if (!email) return false
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return re.test(email.trim())
}

const normalizeEmails = (emails) => {
  if (!emails) return []

  let arr = []

  if (Array.isArray(emails)) {
    arr = emails.map((e) => e?.trim()).filter(Boolean)
  } else if (typeof emails === 'string') {
    arr = emails
      .split(/[;,]+/)
      .map((s) => s.trim())
      .filter(Boolean)
  }
  return arr.filter(isValidEmail)
}

const emailsToString = (arrOrString) => {
  if (!arrOrString) return ''
  if (Array.isArray(arrOrString)) return arrOrString.join(';')
  return arrOrString
}

export default function EmailDialog(props) {
  const open = props.open ?? props.openState ?? false
  const setOpen = props.setOpen ?? props.setOpenState ?? (() => {})
  const isNewEmail = !!props.isNewEmail
  const id = props.id
  const setIsUpdated = props.setIsUpdated ?? (() => {})
  const emailProp = props.email
  const [errors, setErrors] = useState({
    toEmailEmpty: false,
    toEmailInvalid: false,
    ccEmailInvalid: false,
    bccEmailInvalid: false,
    subject: false
  })

  const { user } = useAuth()

  const [mode, setMode] = useState(isNewEmail ? 'compose' : 'view')
  const [email, setEmail] = useState(emailProp || null)
  const [showCc, setShowCc] = useState(false)
  const [showBcc, setShowBcc] = useState(false)
  const [attachments, setAttachments] = useState([])
  const toRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (id && !emailProp) {
        try {
          const res = await getObject('email', id)
          if (!cancelled) setEmail(res.data || null)
        } catch (e) {
          toast.error("Erreur lors du chargement de l'email", { autoClose: 2000 })
        }
      } else {
        setEmail(emailProp || null)
      }
    }
    load()
    return () => (cancelled = true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, emailProp])

  useEffect(() => {
    if (isNewEmail) setMode('compose')
  }, [isNewEmail])

  useEffect(() => {
    if (mode === 'compose') {
      const src = email?.attachments || []
      setAttachments(src)
    }
  }, [mode, email])

  const validateEmails = () => {
    let hasError = false
    const newErrors = {
      toEmailEmpty: false,
      toEmailInvalid: false,
      ccEmailInvalid: false,
      bccEmailInvalid: false,
      subject: false
    }

    const toArr = email?.toEmail
      ? email.toEmail
          .split(/[;,]+/)
          .map((e) => e.trim())
          .filter(Boolean)
      : []
    const ccArr = email?.ccEmail
      ? email.ccEmail
          .split(/[;,]+/)
          .map((e) => e.trim())
          .filter(Boolean)
      : []
    const bccArr = email?.bccEmail
      ? email.bccEmail
          .split(/[;,]+/)
          .map((e) => e.trim())
          .filter(Boolean)
      : []

    if (!toArr.length) {
      newErrors.toEmailEmpty = true
      hasError = true
    } else if (!toArr.every(isValidEmail)) {
      newErrors.toEmailInvalid = true
      hasError = true
    }

    if (ccArr.length && !ccArr.every(isValidEmail)) {
      newErrors.ccEmailInvalid = true
      hasError = true
    }

    if (bccArr.length && !bccArr.every(isValidEmail)) {
      newErrors.bccEmailInvalid = true
      hasError = true
    }

    if (!email?.subject?.trim()) {
      newErrors.subject = true
      hasError = true
    }

    setErrors(newErrors)
    return !hasError
  }

  const doCloseOnly = () => setOpen(false)

  const doSaveDraftThenClose = async () => {
    const toStr = emailsToString(normalizeEmails(email?.toEmail))
    const ccStr = emailsToString(normalizeEmails(email?.ccEmail))
    const bccStr = emailsToString(normalizeEmails(email?.bccEmail))

    const prepared = []
    for (const a of attachments || []) {
      if (a?.content && a?.filename) {
        prepared.push(a)
      } else if (a instanceof File) {
        const content = await fileToBase64(a)
        prepared.push({ filename: a.name, content, encoding: 'base64' })
      }
    }

    const mailOptions = {
      from: user?.email,
      mailbox: user?.email,
      to: toStr || undefined,
      cc: ccStr || undefined,
      bcc: bccStr || undefined,
      subject: email?.subject || '',
      html: email?.html || '',
      priority: 'normal',
      fromName: user?.displayName || user?.email,
      createdBy: user?.email,
      folder: 'DRAFT',
      attachments: prepared.length ? prepared : undefined
    }

    try {
      await addObject('email', { mailOptions })
      if (email?.folder === 'DRAFT' && email?.id) {
        try {
          await deleteObject('email', email.id)
        } catch (_) {}
      }
      setIsUpdated(true)
    } catch (err) {
    } finally {
      setOpen(false)
    }
  }

  const handleSend = async () => {
    if (!validateEmails()) return

    const prepared = []
    for (const a of attachments || []) {
      if (a?.content && a?.filename) {
        prepared.push(a)
      } else if (a instanceof File) {
        const content = await fileToBase64(a)
        prepared.push({ filename: a.name, content, encoding: 'base64' })
      }
    }

    const mailOptions = {
      from: user?.email,
      mailbox: user?.email,
      to: emailsToString(normalizeEmails(email?.toEmail)),
      cc: emailsToString(normalizeEmails(email?.ccEmail)) || undefined,
      bcc: emailsToString(normalizeEmails(email?.bccEmail)) || undefined,
      subject: email?.subject,
      html: email?.html || '',
      priority: 'normal',
      fromName: user?.displayName || user?.email,
      createdBy: user?.email,
      attachments: prepared.length ? prepared : undefined
    }

    try {
      const success = await sendMail(mailOptions)
      if (success) {
        if (email?.folder === 'DRAFT' && email?.id) {
          try {
            await deleteObject('email', email.id)
          } catch (_) {}
        }
        setIsUpdated(true)
        setOpen(false)
      }
    } catch (err) {
      toast.error(err?.message || 'Échec de l’envoi.')
    }
  }

  const replyFromView = () => {
    if (!email) return
    setMode('compose')
    setShowCc(false)
    setShowBcc(false)
    setEmail({
      ...email,
      fromEmail: user?.email,
      toEmail: [email.fromEmail],
      subject: `Re: ${email.subject || ''}`,
      html:
        `<p></p><blockquote type="cite"><div>Le ${email.datetime || ''}, ${email.fromName || ''} &lt;${
          email.fromEmail || ''
        }&gt; a écrit :</div><br>` +
        (email.html || '') +
        `</blockquote>`
    })
  }

  const replyAllFromView = () => {
    if (!email) return
    const toUnique = Array.from(new Set([email.fromEmail, ...normalizeEmails(email.toEmail)])).filter(
      (addr) => addr && addr !== user?.email
    )
    const ccArr = normalizeEmails(email.ccEmail)
    setMode('compose')
    setShowCc(!!ccArr.length)
    setShowBcc(false)
    setEmail({
      ...email,
      fromEmail: user?.email,
      toEmail: toUnique,
      ccEmail: ccArr.length ? ccArr : undefined,
      subject: `Re: ${email.subject || ''}`,
      html:
        `<p></p><blockquote type="cite"><div>Le ${email.datetime || ''}, ${email.fromName || ''} &lt;${
          email.fromEmail || ''
        }&gt; a écrit :</div><br>` +
        (email.html || '') +
        `</blockquote>`
    })
  }

  const forwardFromView = () => {
    if (!email) return
    setMode('compose')
    setShowCc(false)
    setShowBcc(false)
    setEmail({
      ...email,
      fromEmail: user?.email,
      toEmail: [],
      subject: `Fwd: ${email.subject || ''}`,
      html:
        `<p></p><blockquote type="cite"><p>---------- Forwarded message ---------</p>` +
        `<p>De : ${email.fromEmail || ''}</p>` +
        `<p>Date : ${email.datetime || ''}</p>` +
        `<p>Sujet : ${email.subject || ''}</p>` +
        `<p>To : ${emailsToString(normalizeEmails(email.toEmail))}</p>` +
        `${email.html || ''}</blockquote>`
    })
  }

  const isCompose = mode === 'compose'
  const cleanHtml = useMemo(() => DOMPurify.sanitize(email?.html || ''), [email?.html])

  const handleAddFiles = (e) => {
    const files = Array.from(e.target.files || [])
    setAttachments((prev) => [...prev, ...files])
    e.target.value = ''
  }

  const downloadFile = (index) => {
    const file = email.attachments[index].file
    const buffer = Buffer.from(file)
    const blob = new Blob([buffer])
    saveAs(blob, email.attachments[index].filename)
  }

  if (!open) return null

  return (
    <Dialog open={open} fullWidth maxWidth='md' onClose={doCloseOnly}>
      <DialogTitle>
        {isCompose ? (isNewEmail ? 'Nouveau courriel' : email?.subject || 'Rédaction') : email?.subject || 'Message'}
        <IconButton aria-label='close' onClick={doCloseOnly} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {!isCompose && email && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant='subtitle2'>
              <b>De:</b> {email.fromName ? `${email.fromName} <${email.fromEmail}>` : email.fromEmail}
            </Typography>
            <Typography variant='subtitle2'>
              <b>À:</b> {emailsToString(normalizeEmails(email.toEmail))}
            </Typography>
            {!!normalizeEmails(email.ccEmail).length && (
              <Typography variant='subtitle2'>
                <b>CC:</b> {emailsToString(normalizeEmails(email.ccEmail))}
              </Typography>
            )}
            <Paper variant='outlined' sx={{ p: 2, minHeight: 200 }}>
              {cleanHtml ? (
                <div dangerouslySetInnerHTML={{ __html: cleanHtml }} />
              ) : (
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{email?.text || ''}</Typography>
              )}
            </Paper>
            {!!(email.attachments && email.attachments.length) && (
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {email.attachments.map((att, idx) => (
                  <Chip key={idx} label={att.filename || att.name} onClick={() => downloadFile(idx)} />
                ))}
              </Box>
            )}
          </Box>
        )}

        {isCompose && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              inputRef={toRef}
              label='À'
              fullWidth
              value={email?.toEmail || ''}
              onChange={(e) => setEmail({ ...(email || {}), toEmail: e.target.value })}
              error={errors.toEmailEmpty || errors.toEmailInvalid}
              helperText={
                errors.toEmailEmpty ? 'Destinataire obligatoire' : errors.toEmailInvalid ? 'Format email invalide' : ''
              }
            />
            {showCc && (
              <TextField
                label='Cc'
                fullWidth
                value={email?.ccEmail || ''}
                onChange={(e) => setEmail({ ...(email || {}), ccEmail: e.target.value })}
                error={errors.ccEmailInvalid}
                helperText={errors.ccEmailInvalid ? 'Format email invalide' : ''}
              />
            )}
            {showBcc && (
              <TextField
                label='Bcc'
                fullWidth
                value={email?.bccEmail || ''}
                onChange={(e) => setEmail({ ...(email || {}), bccEmail: e.target.value })}
                error={errors.bccEmailInvalid}
                helperText={errors.bccEmailInvalid ? 'Format email invalide' : ''}
              />
            )}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {!showCc && (
                <Button size='small' onClick={() => setShowCc(true)}>
                  +Cc
                </Button>
              )}
              {!showBcc && (
                <Button size='small' onClick={() => setShowBcc(true)}>
                  +Bcc
                </Button>
              )}
            </Box>

            <TextField
              label='Sujet'
              fullWidth
              value={email?.subject || ''}
              onChange={(e) => setEmail({ ...(email || {}), subject: e.target.value })}
              error={errors.subject}
              helperText={errors.subject ? 'Sujet obligatoire' : ''}
            />

            <JoditEditor value={email?.html || ''} onBlur={(val) => setEmail({ ...(email || {}), html: val })} />

            <Button variant='outlined' component='label' startIcon={<AttachFileIcon />}>
              Ajouter des pièces jointes
              <input hidden type='file' multiple onChange={handleAddFiles} />
            </Button>

            {!!attachments.length && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {attachments.map((a, i) => (
                  <Chip
                    key={i}
                    label={a.filename || a.name}
                    onDelete={() => setAttachments((prev) => prev.filter((_, idx) => idx !== i))}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {!isCompose ? (
          <>
            <Tooltip title='Répondre'>
              <IconButton onClick={replyFromView}>
                <ReplyIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title='Répondre à tous'>
              <IconButton onClick={replyAllFromView}>
                <ReplyAllIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title='Transférer'>
              <IconButton onClick={forwardFromView}>
                <ForwardIcon />
              </IconButton>
            </Tooltip>
            <Button onClick={doCloseOnly}>Fermer</Button>
          </>
        ) : (
          <>
            <ConfirmationButton
              message={'Enregistrer le brouillon ?'}
              onClickOk={doSaveDraftThenClose}
              onClickNo={doCloseOnly}
            >
              Annuler
            </ConfirmationButton>
            <Button variant='contained' onClick={handleSend}>
              Envoyer
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
